import { Card, Typography, Avatar, Skeleton, Row, Col } from "antd";
import React, { useMemo } from "react";
import { DeltaTag, NoDataText } from ".";
import { titleCase } from "@/ts/utils";
import { formatMetric } from "@/ts/revstack/helpers";
import { CommonWidgetMetrics } from "@/ts/common-types";

const { Text, Title } = Typography;

interface MetricConfig {
    key: any;
    label: string;
    suffix?: string;
    showChange?: boolean;
}

interface IRevstackConversionOverviewProps {
    widgetConfig: {
        sections: {
            title: string;
            metrics: MetricConfig[];
        }[];
        container: {
            style: {
                height: number;
            };
        };
    };
    widgetData: any;
    title?: string;
}

/**
 * !Note:
 * In this component, there are things that are specific to this module. Since, we don't have an screens
 * That shows future usage of this component, we are sticking to the same nomenclature and filtering in this
 * Once, we have another view in hand we can refactor it and make it more configurable
 */

const RevstackComparisionOverview: React.FC<
    IRevstackConversionOverviewProps
> = ({ widgetConfig, widgetData, title = "Conversion Overview" }) => {
    const { data, isLoading } = widgetData;
    const showNoData = useMemo(() => {
        return data?.length === 0;
    }, [data]);

    const containerHeight = widgetConfig?.container?.style?.height || 400;

    // Separate ALL CHANNELS data from individual channel data
    const allChannelsData = useMemo(() => {
        return data?.find((channel: any) => channel.id === 0);
    }, [data]);

    const individualChannelsData = useMemo(() => {
        return data?.filter((channel: any) => channel.id !== 0) || [];
    }, [data]);

    const renderMetricValue = (channel: any, metric: MetricConfig, justify?: "center" | "start" | "end") => {

        const value = channel[`${metric.key}_value`];
        const changeKey = `${metric.key}_change` as keyof any;
        const changeValue = channel[changeKey];

        if (value === undefined || value === null) {
            return (
                <div className={`w-100 d-flex justify-content-${justify || "center"}`}>
                    <Text className="text-muted">-</Text>
                </div>
            );
        }

        const metricKey = metric.key.includes(CommonWidgetMetrics.MARKET_SHARE) ? CommonWidgetMetrics.MARKET_SHARE: metric.key;

        return (
            <div className="d-flex align-items-center justify-content-between w-100 gap-1 fs-7">
                <Text strong className="text-dark">
                    {formatMetric(metricKey, value, false)}
                </Text>
                {metric.showChange && changeValue !== undefined && (
                    <DeltaTag
                        metric={metric.key}
                        value={changeValue}
                        unit={metric.suffix}
                        className="fw-500"
                        isPositive={
                            typeof changeValue === "number" && changeValue
                                ? changeValue >= 0
                                : null
                        }
                    />
                )}
            </div>
        );
    };

    const renderChannelHeader = (channel: any) => {
        return (
            <div className="d-flex flex-column align-items-center gap-2">
                <Avatar
                    src={channel.icon}
                    alt={channel.name}
                    size={40}
                    shape="square"
                    style={{ borderRadius: "8px" }}
                >
                    {/* Fallback content if no src or src fails */}
                    {!channel.icon && (channel.name?.charAt(0) || "?")}
                </Avatar>
                <Text className="text-dark text-center fs-7 fw-600" style={{ fontSize: "12px" }}>
                    {channel?.name ? titleCase(channel.name) : "N/A"}
                </Text>
            </div>
        );
    };

    if(isLoading) {
        return (
            <Skeleton.Node
                className="w-100"
                active
                style={{ height: containerHeight }}
            />
        );
    }
    
    if (showNoData) {
        return (
            <div
                className="d-flex align-items-center justify-content-center"
                style={{
                    height: containerHeight,
                    // border: noPadding ? "none" : "1px solid #e8e8e8",
                }}
            >
                <NoDataText />
            </div>
        );
    }

    return (
            <Row className="py-2">
                {/* Left Column: ALL CHANNELS */}
                {allChannelsData && (
                    <Col flex="350px">
                        <div className="mx-4">
                        <div style={{ height: "70px", marginBottom: "16px" }}>
                        </div>
                        
                        {widgetConfig?.sections?.map((section) => (
                            <div key={section.title} className="mb-4">
                                {section?.title?.length > 0 && (
                                    <div className="d-flex align-items-center gap-1 mb-2">
                                        <Title
                                            level={5}
                                            className="mb-0 text-dark ms-1 fs-6"
                                        >
                                            {section.title}
                                        </Title>
                                        <div className="bg-frost flex-fill ms-2">
                                            <hr className="border-secondary border-1 m-0"/>
                                        </div>
                                    </div>
                                )}
                                <Card
                                    className="bg-frost rounded-4 border-secondary overflow-hidden"
                                    bodyStyle={{ padding: 0 }}
                                >
                                    {section?.metrics?.map((metric, metricIndex) => (
                                        <div
                                            key={`combined-${metric.key}`}
                                            className="px-3 py-2 d-flex justify-content-between align-items-center"
                                            style={{
                                                borderBottom:
                                                    metricIndex ===
                                                    section?.metrics?.length - 1
                                                        ? "none"
                                                        : "1px solid #e8e8e8",
                                                height: "48px"
                                            }}
                                        >
                                            <Text className="me-3 fs-7" style={{ flex: 1, minWidth: 0, whiteSpace: 'nowrap' }}>{metric.label}</Text>
                                            <div style={{ flexShrink: 0 }}>
                                                {renderMetricValue(allChannelsData, metric, "end")}
                                            </div>
                                        </div>
                                    ))}
                                </Card>
                            </div>
                        ))}
                        </div>
                    </Col>
                )}

                {/* Right Column: Individual Channels with Horizontal Scroll */}
                <Col flex="auto">
                    <Row className="overflow-auto" wrap={false} gutter={16} style={{ marginLeft: 0, marginRight: 0 }}>
                    {individualChannelsData?.map((channel: any) => (
                                <Col span={8} key={channel.id} style={{
                                    maxWidth: "300px"
                                }}>
                                    {/* Channel Header */}
                                    <div className="mb-3">
                                        <div className="d-flex flex-column align-items-center justify-content-center px-3">
                                            {renderChannelHeader(channel)}
                                        </div>
                                    </div>
                                    
                                    {/* Channel Data for all sections */}
                                    {widgetConfig?.sections?.map((section, sectionIndex) => (
                                        <div key={section.title} className="mb-4">
                                            <Card
                                                className={`bg-white rounded-4 border-secondary overflow-hidden`}
                                                bodyStyle={{ padding: 0 }}
                                                style={ sectionIndex !== 0 ? {
                                                    marginTop: 56,
                                                } : {}}
                                            >
                                                {section?.metrics?.map(
                                                    (metric, metricIndex) => (
                                                        <div
                                                            key={`${channel.id}-${metric.key}`}
                                                            className="d-flex align-items-center justify-content-center px-3 py-2"
                                                            style={{
                                                                borderBottom:
                                                                    metricIndex ===
                                                                    section?.metrics?.length - 1
                                                                    ? "none"
                                                                    : "1px solid #e8e8e8",
                                                                height: "48px"
                                                            }}
                                                        >
                                                            {renderMetricValue(channel, metric)}
                                                        </div>
                                                    )
                                                )}
                                            </Card>
                                        </div>
                                    ))}
                                </Col>
                            ))}
                    </Row>
                </Col>
            </Row>
    );
};

export default RevstackComparisionOverview;